import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import RenderTableOrders from "./renderTable";
import EditOrders from "./editOrders";
import { socket } from "../../utils/socket";
import { useNotification } from "../../context/NotificationContext";
import ConfirmDialog from "../../components/ConfirmDialog";

const ORDERS_PER_PAGE = 20;

export default function Orders({ userData }) {
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [aggregatorFilter, setAggregatorFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ✅ Fetch all orders
  const fetchOrders = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders?page=${currentPage}&limit=10`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resdata = await res.json();

      if (!res.ok || !resdata.success) {
        if (res.status === 401) {
          showNotificationRef.current(
            "Session expired. Please login again.",
            "error"
          );
          localStorage.removeItem("token");
          sessionExpiredRef.current = true;
          navigate("/login", { replace: true });
          return;
        }
      }

      if (resdata?.data) {
        setAllOrders(resdata.data);
      } else {
        throw new Error("Invalid orders data format");
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      showNotificationRef.current("Error loading orders", "error");
      setError(err.message);
      setAllOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ✅ Play notification sound
  const playSound = useCallback(() => {
    const audio = new Audio("/assets/audio/notifyneworder.mp3");
    audio.play().catch(() => {});
  }, []);

  // ✅ Listen for real-time new orders
  const handleNewOrder = useCallback(
    (newOrder) => {
      console.log("📦 New order received via Socket.IO:", newOrder);
      playSound();
      showNotificationRef.current("New order received", "success");

      const orderId = newOrder._id || newOrder.id;

      setAllOrders((prev) => {
        const exists = prev.some(
          (o) =>
            o._id === orderId ||
            o.id === orderId ||
            o.orderId === newOrder.orderId
        );
        if (exists) return prev;
        return [newOrder, ...prev];
      });
    },
    [playSound]
  );

  useEffect(() => {
    socket.on("newOrder", handleNewOrder);
    return () => socket.off("newOrder", handleNewOrder);
  }, [handleNewOrder]);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  // ✅ Filter and paginate orders
  const filteredOrders = useMemo(() => {
    let tempOrders = [...allOrders];

    if (aggregatorFilter) {
      tempOrders = tempOrders.filter(
        (order) => order.aggregator === aggregatorFilter
      );
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      tempOrders = tempOrders.filter(
        (order) =>
          String(order.orderId || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.trainNo || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.status || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.trainName || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.aggregator || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.customerName || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.paymentStatus || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.totalAmount || "")
            .toLowerCase()
            .includes(lower) ||
          String(order.subTotal || "")
            .toLowerCase()
            .includes(lower) ||
          order.items?.some((item) =>
            String(item.name || "")
              .toLowerCase()
              .includes(lower)
          )
      );
    }

    return tempOrders;
  }, [allOrders, aggregatorFilter, searchTerm]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  // ✅ Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleAggregatorChange = (agg) => {
    setAggregatorFilter(agg);
    setCurrentPage(1);
  };

  // ✅ Edit handlers
  const handleEdit = (order) => {
    setOrderToEdit(order);
    setIsFormVisible(true);
  };

  const handleCancel = () => {
    setIsFormVisible(false);
    setTimeout(() => setOrderToEdit(null), 300);
  };

  const handleUpdate = async (payload) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders/${payload._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const updatedOrder = await response.json();
      setAllOrders(
        allOrders.map((order) =>
          order._id === updatedOrder._id ? updatedOrder : order
        )
      );

      showNotification("Order updated successfully", "success");
      handleCancel();
    } catch (error) {
      console.error("Error updating order:", error);
      showNotification("Failed to update order", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Delete order handlers
  const handleDelete = (orderId) => {
    setDeleteId(orderId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders/${deleteId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      setAllOrders((prev) => prev.filter((order) => order._id !== deleteId));
      showNotification("Order deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting order:", error);
      showNotification("Failed to delete order", "error");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setIsLoading(false);
    }
  };

  return (
    <section id="orders-section" className="content-body">
      {error && <p style={{ color: "red" }}>Error: {error}</p>}

      {!error && allOrders && (
        <RenderTableOrders
          paginatedOrders={paginatedOrders}
          userData={userData}
          allOrders={allOrders}
          onSearch={handleSearch}
          onAggregatorChange={handleAggregatorChange}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />
      )}

      {orderToEdit && (
        <div
          className={`modal-backdrop ${isFormVisible ? "show" : ""}`}
          onClick={handleCancel}
        >
          <div
            className="order-form-container"
            onClick={(e) => e.stopPropagation()}
          >
            <EditOrders
              order={orderToEdit}
              onSubmit={handleUpdate}
              onCancel={handleCancel}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Order?"
        message="Are you sure you want to delete this order? This action cannot be undone."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </section>
  );
}
